import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import CheckCircle from "@mui/icons-material/CheckCircle";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import QuizIcon from "@mui/icons-material/Quiz";

import { getModuleTutorials, markTutorialComplete } from "./englishApi";
import "../../tutorialContent.css";
const PRIMARY = "#006D77";
const BORDER = "#E0F2F1";

export default function EnglishModulePage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    getModuleTutorials(moduleId)
      .then(setData)
      .catch(() => setError("Couldn't load this module. Please try again."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [moduleId]);

  const toggleComplete = async (tutorial) => {
    const next = !tutorial.completed;
    // optimistic update
    setData((prev) => ({
      ...prev,
      tutorials: prev.tutorials.map((t) => (t.id === tutorial.id ? { ...t, completed: next } : t)),
    }));
    try {
      await markTutorialComplete(tutorial.id, next);
    } catch {
      load(); // revert by refetching on failure
    }
  };
const DRAWER_WIDTH = 280;
if (loading) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 8, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <CircularProgress sx={{ color: PRIMARY }} />
    </Box>
  );
}

if (error) {
  return (
    <Box sx={{ p: 3, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );
}
  const { module, tutorials } = data;

  return (
  <Box sx={{ bgcolor: "#F4F6F8", minHeight: "100vh", p: { xs: 2, md: 3 }, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/english")} size="small">
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {module.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {module.subtitle}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<QuizIcon />}
          onClick={() => navigate(`/english/${moduleId}/quiz`)}
          sx={{ background: "linear-gradient(135deg, #006D77 0%, #00A8B5 100%)" }}
        >
          Practice Quiz
        </Button>
      </Stack>

      <Stack spacing={2.5}>
        {tutorials.map((tutorial) => (
          <Card key={tutorial.id} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, boxShadow: "none" }}>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip label={tutorial.code} size="small" sx={{ bgcolor: "#E0F2F1", color: PRIMARY, fontWeight: 700 }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {tutorial.title}
                  </Typography>
                  {tutorial.badges?.map((b) => (
                    <Chip key={b} label={b} size="small" variant="outlined" />
                  ))}
                </Stack>

                <Tooltip title={tutorial.completed ? "Mark as not done" : "Mark as done"}>
                  <IconButton onClick={() => toggleComplete(tutorial)}>
                    {tutorial.completed ? (
                      <CheckCircle sx={{ color: "#2E7D32" }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ color: "text.disabled" }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Rich tutorial content, extracted from the original course material.
                  Sanitized with DOMPurify before rendering. Styled via tutorialContent.css,
                  scoped under .tutorial-content so it can't leak into the rest of the app. */}
              <Box
                className="tutorial-content"
                sx={{ p: { xs: 2, md: 3 } }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(tutorial.content_html),
                }}
              />
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
